import { cn } from "@fatbees/ui-utils";
import { ChevronRightIcon } from "lucide-react";
import { forwardRef, MouseEvent, ReactNode } from "react";

export interface ExpandoCardProps {
  /** Whether the body is expanded. */
  open: boolean;
  onToggle: () => void;
  /**
   * Disables the click-to-toggle behaviour (e.g. while a name inside the
   * header is being edited inline).
   */
  toggleDisabled?: boolean;
  /** Header content: name, subtitle, status icons. Clicking it toggles. */
  header: ReactNode;
  /**
   * Right-aligned controls (usually a `RowMenu`). Clicks here do not
   * toggle the card.
   */
  actions?: ReactNode;
  /** Expanded body, rendered under a top border when `open`. */
  children?: ReactNode;
  className?: string;
  /** Extra classes for the expanded body container. */
  bodyClassName?: string;
}

/**
 * A collapsible card for lists of entities (devices, loads, …) on
 * management pages. Visually it follows the `Panel` card language
 * (white, `rounded-lg`, `border-neutral-200`); interaction-wise the whole
 * header row toggles the body, with a keyboard-accessible chevron button.
 *
 * Stack ExpandoCards in a `flex flex-col gap-2.5` container — the card
 * itself has no outer margin.
 */
export const ExpandoCard = forwardRef<HTMLDivElement, ExpandoCardProps>(
  function ExpandoCard(
    {
      open,
      onToggle,
      toggleDisabled,
      header,
      actions,
      children,
      className,
      bodyClassName,
    },
    ref,
  ) {
    function handleToggle() {
      if (!toggleDisabled) {
        onToggle();
      }
    }

    return (
      <div
        ref={ref}
        data-slot="expando-card"
        className={cn(
          "rounded-lg border border-neutral-200 bg-white overflow-hidden",
          className,
        )}
      >
        <div
          className={cn(
            "flex items-center gap-2.5 py-3 px-4 select-none",
            toggleDisabled ? "cursor-default" : "cursor-pointer",
          )}
          onClick={handleToggle}
        >
          <button
            type="button"
            aria-expanded={open}
            className="shrink-0 cursor-pointer"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              handleToggle();
            }}
          >
            <ChevronRightIcon
              className={cn(
                "size-3 transition-transform duration-200",
                open && "rotate-90",
              )}
            />
            <span className="sr-only">{open ? "Collapse" : "Expand"}</span>
          </button>
          <div className="flex-1 min-w-0">{header}</div>
          {actions && <div onClick={(e) => e.stopPropagation()}>{actions}</div>}
        </div>
        {open && (
          <div className={cn("border-t border-neutral-100", bodyClassName)}>
            {children}
          </div>
        )}
      </div>
    );
  },
);
